/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * Reading an `includedEntityGroup` / `excludedEntityGroup` edit as the act it was.
 *
 * Alert subscriptions and alert configurations both select what they apply to through the same two
 * fields, and the writer records an edit to them several different ways depending on what changed:
 * adding one entry is `includedEntityGroup.ids.1` with the new id; swapping one is the same path
 * with both sides; replacing the selection writes the whole object; and switching mode writes
 * `groupType` plus, for a saved group, `id`. Read as a flat diff those look like four unrelated
 * kinds of change when all a person did was pick different assets — and a three-asset addition to a
 * list of eight arrives as nine indexed entries, most of which say nothing.
 *
 * This module reduces all of those forms to one membership delta, which both renderers present.
 */

import type { NormalizedChange } from '../diff/normalize'

export const ENTITY_GROUP_ROOTS = ['includedEntityGroup', 'excludedEntityGroup'] as const
export type EntityGroupRoot = (typeof ENTITY_GROUP_ROOTS)[number]

export const isEntityGroupChange = (change: NormalizedChange): boolean =>
  (ENTITY_GROUP_ROOTS as readonly string[]).includes(change.segments[0])

/**
 * What an entity group's ids point at.
 *
 * An alert configuration selects assets *or* monitoring points through the same field — a door
 * alert is configured against monitoring points — so a panel that says "assets" for both
 * misdescribes half of them. The kind is carried in the id itself.
 */
export type EntityKind = 'asset' | 'monitoring-point' | 'unknown'

const KIND_PATTERN = /^entity:([a-z-]+):/

export const entityKindOf = (raw: unknown): EntityKind => {
  const kind = typeof raw === 'string' ? KIND_PATTERN.exec(raw)?.[1] : undefined
  return kind === 'asset' || kind === 'monitoring-point' ? kind : 'unknown'
}

/** The noun for a set of ids, decided by what they actually are. Mixed sets read as "entities". */
export const entityNoun = (ids: string[], kinds: EntityKind[]): string => {
  const distinct = new Set(kinds.filter((kind) => kind !== 'unknown'))
  if (distinct.size === 1) {
    const [kind] = [...distinct]
    const plural = kind === 'monitoring-point' ? 'monitoring points' : 'assets'
    return ids.length === 1 ? plural.replace(/s$/, '') : plural
  }
  return distinct.size > 1 ? 'entities' : 'assets'
}

/**
 * The Fresh numeric id inside a namespaced identifier.
 *
 * `user:sql46367` → `46367`, `entity:asset:sql291901` → `291901`. When no `sql<number>` marker is
 * present the last colon-separated segment is used as a last resort.
 */
export const stripSqlId = (raw: unknown): string => {
  const s = String(raw ?? '')
  const match = /sql(\d+)/i.exec(s)
  if (match) return match[1]
  const parts = s.split(':')
  return parts[parts.length - 1] || s
}

/** The numeric id in a company reference such as `company:sql102`, or undefined when there is none. */
export const companyIdOf = (raw: unknown): number | undefined => {
  if (typeof raw !== 'string' && typeof raw !== 'number') return undefined
  const id = Number(stripSqlId(raw))
  return Number.isFinite(id) && id > 0 ? id : undefined
}

export const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null

export type Group = { groupType?: string; ids?: unknown[]; id?: unknown; companyId?: unknown } | null

/** One entry of a group's membership, keeping the kind alongside the id. */
export interface GroupMember {
  id: string
  kind: EntityKind
}

/**
 * A zone-based monitoring point, e.g. `entity:monitoring-point:/asset/sql249560/zone/1/return-air`.
 *
 * It is not a Fresh `MonitoringPoint` row: the only `sql<number>` in it is the *asset's* id, so
 * reading it like a plain point id looks up the wrong thing and shows the asset's number. Its name
 * lives in the entity store, which this app cannot reach, and we deliberately do not derive one from
 * the id, so the path itself is the id and is shown as is.
 */
const ZONE_POINT_PATTERN = /^entity:monitoring-point:(\/asset\/sql\d+\/zone\/.+)$/i

/** One group entry as a member, however it is addressed. */
export const memberOf = (raw: unknown): GroupMember => {
  const zone = typeof raw === 'string' ? ZONE_POINT_PATTERN.exec(raw) : null
  return zone ? { id: zone[1], kind: 'monitoring-point' } : { id: stripSqlId(raw), kind: entityKindOf(raw) }
}

/** What came and went from a set of strings. */
export interface StringSetDelta {
  added: string[]
  removed: string[]
}

export interface GroupDelta {
  added: GroupMember[]
  removed: GroupMember[]
  typeBefore?: unknown
  typeAfter?: unknown
  idBefore?: unknown
  idAfter?: unknown
  /** The company owning the selected assets (`company:sql102`), when the edit changed it. */
  companyBefore?: unknown
  companyAfter?: unknown
  /** Name patterns, for a `MonitoringPointsByName` selection. */
  names?: StringSetDelta
  excludedNames?: StringSetDelta
  /** The asset group a by-name selection is scoped to, when the edit changed it. */
  assetGroupBefore?: Group
  assetGroupAfter?: Group
  /** Anything under the group we have no better presentation for. */
  other: NormalizedChange[]
}

const asStrings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : []

/** The difference between two string sets, ignoring order. */
const stringSetDelta = (before: unknown, after: unknown): StringSetDelta => {
  const b = asStrings(before)
  const a = asStrings(after)
  return {
    added: a.filter((entry) => !b.includes(entry)),
    removed: b.filter((entry) => !a.includes(entry)),
  }
}

const isEmptyDelta = (delta: StringSetDelta): boolean => delta.added.length === 0 && delta.removed.length === 0

const idsOf = (group: Record<string, unknown> | null): unknown[] => (Array.isArray(group?.ids) ? group.ids : [])

const toMembers = (raw: unknown[]): GroupMember[] => raw.map(memberOf)

/** Membership one side holds that the other does not. */
const missingFrom = (list: GroupMember[], other: GroupMember[]): GroupMember[] =>
  list.filter((member) => !other.some((candidate) => candidate.id === member.id))

/** Reduce every change under one group root to a single membership delta. */
export const buildGroupDelta = (changes: NormalizedChange[]): GroupDelta => {
  const delta: GroupDelta = { added: [], removed: [], other: [] }

  const applySets = (before: GroupMember[], after: GroupMember[]) => {
    delta.removed.push(...missingFrom(before, after))
    delta.added.push(...missingFrom(after, before))
  }

  for (const change of changes) {
    const [, field] = change.segments

    // The whole group replaced at once — read it as the membership that came and went.
    if (field === undefined) {
      const before = asRecord(change.before)
      const after = asRecord(change.after)
      applySets(toMembers(idsOf(before)), toMembers(idsOf(after)))
      if (before?.groupType !== after?.groupType) {
        delta.typeBefore = before?.groupType
        delta.typeAfter = after?.groupType
      }
      if (before?.id !== after?.id) {
        delta.idBefore = before?.id
        delta.idAfter = after?.id
      }
      if (before?.companyId !== after?.companyId) {
        delta.companyBefore = before?.companyId
        delta.companyAfter = after?.companyId
      }

      const names = stringSetDelta(before?.names, after?.names)
      if (!isEmptyDelta(names)) delta.names = names
      const excluded = stringSetDelta(before?.excludedNames, after?.excludedNames)
      if (!isEmptyDelta(excluded)) delta.excludedNames = excluded

      if (JSON.stringify(before?.assetGroup) !== JSON.stringify(after?.assetGroup)) {
        delta.assetGroupBefore = asRecord(before?.assetGroup) as Group
        delta.assetGroupAfter = asRecord(after?.assetGroup) as Group
      }
      continue
    }

    // A by-name selection matches monitoring points against these patterns rather than naming
    // them, so its membership can change without the configuration being edited. The patterns are
    // therefore the edit, and they diff as sets like everything else here.
    if (field === 'names' || field === 'excludedNames') {
      const [, , index] = change.segments
      const isSlot = index !== undefined
      const patch = isSlot
        ? {
            added: typeof change.after === 'string' ? [change.after] : [],
            removed: typeof change.before === 'string' ? [change.before] : [],
          }
        : stringSetDelta(change.before, change.after)

      const existing = delta[field] ?? { added: [], removed: [] }
      delta[field] = { added: [...existing.added, ...patch.added], removed: [...existing.removed, ...patch.removed] }
      continue
    }

    if (field === 'assetGroup') {
      delta.assetGroupBefore = asRecord(change.before) as Group
      delta.assetGroupAfter = asRecord(change.after) as Group
      continue
    }

    if (field === 'ids') {
      // Either the whole list (`ids`) or one slot in it (`ids.1`).
      if (Array.isArray(change.before) || Array.isArray(change.after)) {
        applySets(
          toMembers(Array.isArray(change.before) ? change.before : []),
          toMembers(Array.isArray(change.after) ? change.after : [])
        )
        continue
      }
      if (change.before !== undefined) {
        delta.removed.push(memberOf(change.before))
      }
      if (change.after !== undefined) {
        delta.added.push(memberOf(change.after))
      }
      continue
    }

    if (field === 'groupType') {
      delta.typeBefore = change.before
      delta.typeAfter = change.after
      continue
    }

    if (field === 'id') {
      delta.idBefore = change.before
      delta.idAfter = change.after
      continue
    }

    // The asset owner. It is often the record's own company, but not always: an alert owned by a
    // parent company can watch a child company's assets, so a change here is a real edit.
    if (field === 'companyId') {
      delta.companyBefore = change.before
      delta.companyAfter = change.after
      continue
    }

    delta.other.push(change)
  }

  // Reordering a list rewrites slots without changing membership: an id on both sides moved, it did
  // not come or go, and showing it as both added and removed would invent two edits from none.
  const unchanged = new Set(
    delta.added.filter((member) => delta.removed.some((other) => other.id === member.id)).map((m) => m.id)
  )
  const dedupe = (members: GroupMember[]) => {
    const seen = new Set<string>()
    return members.filter((member) => {
      if (unchanged.has(member.id) || seen.has(member.id)) return false
      seen.add(member.id)
      return true
    })
  }

  delta.added = dedupe(delta.added)
  delta.removed = dedupe(delta.removed)

  return delta
}

/** Every id the delta names, for batched name resolution. */
export const deltaMembers = (delta: GroupDelta): GroupMember[] => [...delta.added, ...delta.removed]

/** How many edits the delta represents, for the section's count badge. */
const setSize = (delta?: StringSetDelta): number => (delta ? delta.added.length + delta.removed.length : 0)

export const deltaCount = (delta: GroupDelta): number =>
  delta.added.length +
  delta.removed.length +
  (delta.typeAfter !== undefined || delta.typeBefore !== undefined ? 1 : 0) +
  (delta.companyAfter !== undefined || delta.companyBefore !== undefined ? 1 : 0) +
  setSize(delta.names) +
  setSize(delta.excludedNames) +
  (delta.assetGroupAfter !== undefined || delta.assetGroupBefore !== undefined ? 1 : 0) +
  delta.other.length

/**
 * The screen's words for the selection modes, which the entity stores in its own spelling.
 *
 * The two monitoring-point modes are the ones an alert configuration most often uses — together
 * they account for more than half of all live configurations — and they are not interchangeable:
 * `MonitoringPointsById` holds a fixed list of points, while `MonitoringPointsByName` holds name
 * patterns that are matched afresh each time, so the set of points it covers can change without the
 * configuration being edited at all.
 */
const GROUP_TYPE_LABELS: Record<string, string> = {
  AllAssets: 'All assets',
  IndividualAssets: 'Individual assets',
  SmartGroup: 'Smart group',
  MonitoringPointsById: 'Selected monitoring points',
  MonitoringPointsByName: 'Monitoring points matching a name',
}

export const groupTypeLabel = (value: unknown): string => {
  const key = typeof value === 'string' ? value : ''
  if (!key) return 'None'
  return GROUP_TYPE_LABELS[key] ?? key
}

/**
 * The individual entities a group names.
 *
 * Keyed off the presence of `ids` rather than off `IndividualAssets` alone: `MonitoringPointsById`
 * holds its selection the same way, and 290 live configurations use it, so testing for one group
 * type would show those as having selected nothing.
 */
export const individualMembers = (group: Group): GroupMember[] =>
  Array.isArray(group?.ids) ? toMembers(group.ids) : []

/** The name patterns a `MonitoringPointsByName` group matches on, and the ones it excludes. */
export const groupNamePatterns = (group: Group): { names: string[]; excludedNames: string[] } => {
  const record = group as Record<string, unknown> | null
  return {
    names: Array.isArray(record?.names) ? record.names.filter((n): n is string => typeof n === 'string') : [],
    excludedNames: Array.isArray(record?.excludedNames)
      ? record.excludedNames.filter((n): n is string => typeof n === 'string')
      : [],
  }
}

/** The asset group a `MonitoringPointsByName` selection is scoped to, if any. */
export const nestedAssetGroup = (group: Group): Group => {
  const record = group as Record<string, unknown> | null
  return asRecord(record?.assetGroup) as Group
}
